import styles from './FolderItemFigure.module.css';
import React, { MouseEventHandler, ReactNode } from 'react';
import type { BasicFile } from '@he-novation/config/types/file.types';
import { Icon } from '@he-novation/design-system/components/graphics/Icon/Icon';
import { Loader, LoaderSize } from '@he-novation/design-system/components/widgets/Loader/Loader';
import { ProgressCircle } from '@he-novation/design-system/components/widgets/ProgressCircle/ProgressCircle';
import { PulsatingCircle } from '@he-novation/design-system/components/widgets/PulsatingCircle/PulsatingCircle';
import {
    colorAlert,
    colorBgDark,
    colorFolderItemIcon
} from '@he-novation/design-system/constants/constants.style';
import {
    FrontFolderContentFile,
    FrontFolderContentFileStatus,
    FrontFolderContentFileUploading
} from '@he-novation/front-shared/types/file.front-types';
import { FrontFolderContentGhost } from '@he-novation/front-shared/types/folder.front-types';
import { isVideoType } from '@he-novation/utils/mimeType.utils';
import cn from 'classnames';

import { FolderItemVideo } from '$components/DataLayout/DataLayoutFolder/FolderItemFigure/FolderItemVideo';

export enum FolderItemFigureMode {
    Grid = 'grid',
    List = 'list'
}

type FolderItemFigureProps = {
    className?: string;
    item: FrontFolderContentFile | FrontFolderContentGhost | BasicFile;
    onClick?: MouseEventHandler;
    noDuration?: boolean;
    mode?: FolderItemFigureMode;
};

export function FolderItemFigure({
    className,
    item,
    onClick,
    noDuration,
    mode = FolderItemFigureMode.List
}: FolderItemFigureProps) {
    return (
        <div className={cn(styles.folderItemFigure, mode, className)} onClick={onClick}>
            <FolderItemFigureContent item={item} mode={mode} noDuration={noDuration} />
        </div>
    );
}

type FolderItemFigureContentProps = {
    item: FrontFolderContentGhost | FrontFolderContentFile | BasicFile;
    mode: FolderItemFigureMode;
    noDuration?: boolean;
};

function FolderItemFigureContent({
    item,
    mode,
    noDuration
}: FolderItemFigureContentProps): ReactNode {
    const loaderSize = mode === FolderItemFigureMode.Grid ? LoaderSize.Big : LoaderSize.Medium;

    if (item.type === 'ghost') {
        return <Loader circleColor={colorBgDark} size={loaderSize} />;
    }

    if (item.type === 'basic-file') {
        const content =
            item.thumbnail && !item.isLoading ? (
                <img src={item.thumbnail} alt="thumbnail" />
            ) : (
                <Icon icon={{ name: 'file_plain', fill: colorFolderItemIcon, stroke: 'none' }} />
            );

        return (
            <>
                {content} <Loader className={styles.basicFileLoader} size={LoaderSize.Small} />
            </>
        );
    }

    if (item.uploadError) {
        return <Icon icon={{ name: 'question_mark', fill: 'none', stroke: colorAlert }} />;
    }

    const fileItem = item as FrontFolderContentFile;

    switch (fileItem.processingStatus?.status) {
        case FrontFolderContentFileStatus.WAITING_FOR_UPLOAD:
            return <Loader circleColor={colorBgDark} size={loaderSize} />;
        case FrontFolderContentFileStatus.UPLOADING: {
            const status = item.processingStatus as FrontFolderContentFileUploading;

            return <ProgressCircle value={status?.uploadProgress} total={100} />;
        }
        case FrontFolderContentFileStatus.WAITING_FOR_TRANSCODE:
            return <PulsatingCircle className={styles.pulsatingCircle} />;
        case FrontFolderContentFileStatus.TRANSCODING:
        case FrontFolderContentFileStatus.COPYING:
            if (item.isPlayable) {
                return (
                    <div className={styles.transcodingAndPlayable}>
                        {fileItem.thumbnail ? (
                            <img src={fileItem.thumbnail} alt={'thumbnail'} />
                        ) : undefined}
                        {mode === FolderItemFigureMode.Grid && <Loader size={LoaderSize.Small} />}
                    </div>
                );
            }

            return (
                <div className={styles.transcodingWithThumbnail}>
                    {fileItem.thumbnail ? (
                        <img src={fileItem.thumbnail} alt={'thumbnail'} />
                    ) : undefined}
                    <Loader size={loaderSize} />
                </div>
            );
    }

    if (item.isPlayable && item.preview && isVideoType(item.mimeType)) {
        return <FolderItemVideo item={item} noDuration={noDuration} />;
    }

    if (item.isPlayable && item.thumbnail) {
        return <img src={item.thumbnail} alt="thumbnail" />;
    }

    return (
        <FolderItemFigureContentImage
            name={item.name}
            mimeType={item.mimeType}
            isPlayable={item.isPlayable}
        />
    );
}

type FolderItemFigureContentImageProps = {
    name: string;
    mimeType?: string | null;
    isPlayable?: boolean;
    defaultIconColor?: string;
    className?: string;
};

export function FolderItemFigureContentImage({
    name,
    mimeType,
    isPlayable,
    defaultIconColor,
    className
}: FolderItemFigureContentImageProps): ReactNode {
    if (isPlayable) {
        if (mimeType?.startsWith('application/vnd.openxmlformats-officedocument.')) {
            if (/\.pptx?$/.test(name)) {
                return (
                    <img
                        src="/assets/img/powerpoint-icon.png"
                        alt=""
                        className={cn(styles.logo, className)}
                    />
                );
            }
            if (/\.docx?$/.test(name)) {
                return (
                    <img
                        src="/assets/img/word-icon.png"
                        alt=""
                        className={cn(styles.logo, className)}
                    />
                );
            }
            if (/\.xlsx?$/.test(name)) {
                return (
                    <img
                        src="/assets/img/excel-icon.png"
                        alt=""
                        className={cn(styles.logo, className)}
                    />
                );
            }
        }

        if (mimeType === 'text/markdown') {
            return (
                <img
                    src="/assets/img/markdown-icon.png"
                    alt=""
                    className={cn(styles.logo, className)}
                />
            );
        }

        if (mimeType === 'application/zip') {
            return (
                <img src="/assets/img/web-icon.png" alt="" className={cn(styles.logo, className)} />
            );
        }

        if (mimeType?.startsWith('model/')) {
            return (
                <img src="/assets/img/3D-icon.png" alt="" className={cn(styles.logo, className)} />
            );
        }
    }

    return (
        <Icon
            icon={{
                name: 'file_plain',
                fill: defaultIconColor || colorFolderItemIcon,
                stroke: 'none'
            }}
            className={className}
        />
    );
}
